import { json } from "@remix-run/node";
import { type MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import DoneIcon from "@mui/icons-material/Done";
import { Box, Container, Link, Stack, Typography } from "@mui/material";

import { Footer } from "~/components/Footer";
import { Header } from "~/components/Header";
import createMdLoader from "~/components/mdLoader";
import safeLink from "~/components/safeLink";
import Vetusite from "~/public/imgs/cgu/vetusite.png";

export default () => {
  const { data } = useLoaderData<typeof loader>();
  const cguMdMapper = createMdLoader({
    h1: (props, children) => (
      <Typography key={props.key} component="h1" variant="h2" color="secondary">
        {children}
      </Typography>
    ),
    h2: (props, children) => (
      <Typography
        key={props.key}
        component="h2"
        color="primary"
        mt={4}
        mb={2}
        fontSize={32}
        fontWeight={500}
      >
        {children}
      </Typography>
    ),
    h3: (props, children) => (
      <Typography key={props.key} component="h3" variant="h3" my={2}>
        {children}
      </Typography>
    ),
    h4: (props, children) => (
      <Typography
        key={props.key}
        component="h4"
        fontSize={20}
        fontWeight={500}
        marginTop={2}
        marginLeft={4}
        marginBottom={-4}
      >
        {children}
      </Typography>
    ),
    h5: (props, children) => (
      <Typography
        key={props.key}
        component="h4"
        fontSize={18}
        fontWeight={500}
        marginTop={4}
        marginLeft={4}
      >
        {children}
      </Typography>
    ),
    p: (props, children) => (
      <Typography key={props.key} component="p" py={1} lineHeight={1.2}>
        {children}
      </Typography>
    ),
    a: (props, children) => (
      <Link
        key={props.key}
        underline={props.href.includes("mailto") ? "none" : "always"}
        color="primary"
        {...safeLink(props.href)}
      >
        {children}
      </Link>
    ),
    ul: (props, children) => (
      <Stack
        key={props.key}
        className="list"
        pl={4}
        direction="column"
        width="100%"
      >
        {children}
      </Stack>
    ),
    li: (props, children) => {
      let sublist;
      if (Array.isArray(children)) {
        const sublistIndex = children.findIndex(
          ({ props }) => props?.className === "list",
        );
        sublist = children.splice(sublistIndex, 1);
      }
      return (
        <Box key={props.key}>
          <Stack direction="row">
            <DoneIcon color="primary" sx={{ mr: 1 }} />
            {children}
          </Stack>
          {sublist}
        </Box>
      );
    },
    img: () => {
      return (
        <Stack>
          <Box component="img" src={Vetusite} width="70%" mx="auto" mt={2} />
        </Stack>
      );
    },
  });
  return (
    <>
      <Header static />
      <Container component="section" maxWidth="lg">
        <Box pt={16}>{cguMdMapper(data)}</Box>
      </Container>
      <Footer />
    </>
  );
};

export const loader = async () =>
  import("~/data/pages/conditions-generales-dutilisation.md")
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .then((mod) => (mod.default as any)())
    .then((data) => ({ data }))
    .then(json);

export const meta: MetaFunction<typeof loader> = ({ data }) => ({
  data,
});
